import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Detector } from 'react-detect-offline';
import { Route, HashRouter, useLocation, useHistory } from 'react-router-dom';
import { CircleSpinner } from 'react-spinners-kit';
import { appReady, fetchAppData } from '../actions/appActions';
import { initializeApp } from 'firebase/app';
import { DataManagerProvider } from '../context/DataManager';
import { AssociateRealtimeProvider } from '../context/AssociateRealtimeProvider';
import { useTranslation } from 'react-i18next';
import useAppReload from '../hooks/useAppReload';
import useVisibilityChange from '../hooks/useVisibilityChange';

import ContextProvider from '../context/ContextProvider';
import ForgotPassword from '../screens/Login/ForgotPassword/ForgotPassword';
import SignUpScreen from '../screens/SignUp/SignUpScreen';
import SignUpEmailValidationScreen from '../screens/SignUp/SignUpEmailValidationScreen';
import Menu from '../screens/User/Menu/Menu';
import FrontValidateKyc from '../screens/User/Kyc/ValidateScreen/FrontValidateKyc';
import StartKyc from '../screens/User/Kyc/ValidateScreen/StartKyc';

import BackValidateKyc from '../screens/User/Kyc/ValidateScreen/BackValidateKyc';
import SelfieValidateKyc from '../screens/User/Kyc/ValidateScreen/SelfieValidateKyc';
import FinishKyc from '../screens/User/Kyc/ValidateScreen/FinishKyc';
import DeleteAccountScreen from '../screens/User/DeleteAccountScreen/DeleteAccountScreen';
import Login from '../screens/Login/LoginScreen/LoginScreen';
import UserProfile from '../screens/User/Profile/UserProfile';
import PointsScreen from '../screens/User/Points/PointsScreen';
import PointsDetailsScreen from '../screens/User/Points/PointsDetailsScreen';
import PointsActivityScreen from '../screens/User/Points/PointsActivityScreen';
import Payments from '../screens/User/Payments/Payments';
import AllReservations from '../screens/Reservation/AllReservations/AllReservations';
import AuthRoute from './Routes/AuthRoute';
import Auth from './Auth';
import Account from '../screens/User/Account/Account';
import AllPayments from '../screens/User/Payments/AllPayments';
import Venues from './Venues/Venues';
import RefreshIcon from './icons/RefreshIcon';
import KycStatus from '../screens/User/Kyc/ValidateScreen/KycStatus';
import Notifications from '../screens/User/Notifications/Notifications';
import ReservationSuccess from '../screens/Reservation/ReservationSuccess/ReservationSuccess';
import Configuration from '../screens/User/NotificationsConfiguration/Configuration';
import ReservationsDisputed from './ReservationsDisputed/ReservationsDisputed';
import CitySelector from './CitySelector/CitySelector';

import '../css/bootstrap-customization.css';
import '../css/fonts-inter.css';
import '../css/colors.css';
import '../css/style.css';
import AccountCashier from '../screens/Cashier/AccountCashier';
import QRReaderScreen from '../screens/Cashier/QRReaderScreen';
import CashierOperation from '../screens/Cashier/CashierOperation';

import Activation from '../screens/Activate/Activation';
import Withdrawal from '../screens/Withdrawals/Withdrawal';

import Unauthorized from '../screens/User/Unauthorized/Unauthorized';
import MyAffiliates from '../screens/UserRp/MyAffiliates';
import WelcomeHomeScreen from '../screens/User/WelcomeScreens/WelcomeHomeScreen';
import Onboarding from '../screens/User/WelcomeScreens/Onboarding';
import SignUpPhoneValidationScreen from '../screens/SignUp/SignUpPhoneValidationScreen';
import LoginPassword from '../screens/Login/LoginPasswordScreen/LoginPasswordScreen';

import CashierPayments from '../screens/Cashier/CashierPayments';
import SignUpTerms from '../screens/SignUp/SignUpTerms';
import TermsAndConditions from '../screens/SignUp/TermsAndConditions';
import PrivacyPolicy from '../screens/SignUp/PrivacyPolicy';

import Reservation from '../screens/Reservation/Reservation';
import BalancePerMonthScreen from '../screens/User/BalancePerMonth/BalancePerMonthScreen';
import BalancePerMonthDetailScreen from '../screens/User/BalancePerMonth/BalancePerMonthDetailScreen';
import ModifyReservation from '../screens/Reservation/ModifyReservation/ModifyReservation';
import SignUpConciergeScreen from '../screens/SignUp/SignUpConciergeScreen';
import LinkMercadoPagoAccount from '../screens/User/LinkMercadoPagoAccount/LinkMercadoPagoAccount';
import LinkStripeConnect from '../screens/User/LinkStripeConnect/LinkStripeConnect';
import LinkBankNational from '../screens/User/LinkBankNational/LinkBankNational';
import LinkBankInternational from '../screens/User/LinkBankInternational/LinkBankInternational';
import ReservationType from '../screens/Reservation/ReservationType/ReservationType';
import ReservationArea from '../screens/Reservation/ReservationArea/ReservationArea';
import ReservationAreaExtra from '../screens/Reservation/ReservationAreaExtra/ReservationAreaExtra';
import CashierActivityDetail from '../screens/Cashier/CashierActivityDetail';
import ReservationDetailScreen from '../screens/Reservation/ReservationDetailScreen/ReservationDetailScreen';
import LoginUserScreen from '../screens/Login/LoginUserScreen/LoginUserScreen';
import MyMoneyDetail from '../screens/User/MyMoneyDetail/MyMoneyDetail';
import UserChangePasswordScreen from '../screens/User/UserChangePasswordScreen/UserChangePasswordScreen';
import OfflineScreen from '../screens/Common/OfflineScreen';

import FrequentQuestions from '../screens/User/FrequentQuestions/FrequentQuestions';
import RouteReservationDependant from './Routes/RouteReservationDependant';
import OnboardingCampaigns from '../screens/User/Campaigns/OnboardingCampaigns/OnboardingCampaigns';
import CampaignsFeed from '../screens/User/Campaigns/CampaignsFeed/CampaignsFeed';

import { saveToken } from '../services/firebase/pushNotifications';
import LinkQrRestaurant from '../screens/User/LinkQr/LinkQrRestaurant/LinkQrRestaurant';
import LockIcon from './icons/LockIcon';
import SignUpPersonalInformation from '../screens/SignUp/SignUpPersonalInformation';
import MarketSelector from '../screens/SignUp/MarketSelector';
import AccountChecker from '../screens/Checker/AccountChecker';
import CheckShow from '../screens/Checker/CheckerShow/CheckShow';
import CheckInQr from './CheckingQr/CheckInQr';
import ScanQrChecker from './ScanQrChecker/ScanQrChecker';
import ScanQrCheckerError from './ScanQrChecker/ScanQrCheckerError';
import ScanQrCheckerExit from './ScanQrChecker/ScanQrCheckerExit';
import ReservationCheckTickets from '../screens/Reservation/ReservationCheck/ReservationCheckTickets';
import ReservationCheck from '../screens/Reservation/ReservationCheck/ReservationCheck';
import ReservationTicketSelector from '../screens/Reservation/ReservationTicketSelector/ReservationTicketSelector';
import VenueDetail from '../screens/Venue/VenueDetail/VenueDetail';
import EventDetail from '../screens/Event/EventDetail/EventDetail';
import ManualQrChecker from './ScanQrChecker/ManualQrChecker';
import ManualQrCheckerExit from './ScanQrChecker/ManualQrCheckerExit';
import ListQrCheckin from './ListQrCheckin/ListQrCheckin';
import ReservationSuccessCourtesy from '../screens/Reservation/ReservationSuccessCourtesy/ReservationSuccessCourtesy';
import Home from '../screens/Customer/Home/Home';
import CreateCard from './CreateCard/CreateCard';
import EventSelector from '../screens/Reservation/EventSelector/EventSelector';
import ScanPaymentScreen from './Payments/ScanPayment';
import TicketsDetails from './TicketsDetails/TicketsDetails';
import Etickets from '../screens/Customer/Home/Etickets';
import EventsCustomer from '../screens/Customer/Home/EventsCustomer';
import PayConfirmConsumer from './PaymentCustomer/PayConfirmConsumer.js';
import TicketsDetailsSuccess from './TicketsDetails/TicketsDetailsSucces.js';
import NotificationsCustomer from '../screens/Customer/Home/NotificationsCustomer.js';
import ConsumeSummary from '../screens/Customer/ConsumeSummary/ConsumeSummary.js';
import ConsumePay from '../screens/Customer/ConsumePay/ConsumePay.js';
import Success from '../screens/Success/Success.js';
import useScrollHack from '../hooks/useScrollHack.js';
import LoyaltyCardCustomer from './Consumer/LoyaltyCard/LoyaltyCardCustomer.js';
import ReservationNoGuaranteed from '../screens/Reservation/ReservationNoGuaranteed/ReservationNoGuaranteed.js';
import SignUpEmailValidationScreenCustomer from '../screens/Customer/SignUpEmailValidationCustomer/SignUpEmailValidationScreenCustomer.js';
import SignUpCreatePasswordCustomer from '../screens/Customer/SignUpCreatePasswordCustomer/SignUpCreatePasswordCustomer.js';
import SeeCards from './Consumer/SeeCards/SeeCards.js';
import AddAccount from '../screens/Stripe/AddAccount.js';
import ConfirmAddAccount from '../screens/Stripe/ConfirmAddAccount.js';
import SuccessAddCount from '../screens/Stripe/Success.js';
import PersonalInformationForm from '../screens/Stripe/PersonalInformationForm.js';
import LinkHome from '../screens/Link/Home/Home.js';
import LinkForm from '../screens/Link/Form/Form.js';
import LinkConfirmation from '../screens/Link/Confirmation/Confirmation.js';
import LinkPayments from '../screens/Link/Payments/Payments.js';
import ReservationDetailScreenLink from '../screens/Reservation/ReservationDetailScreen/ReservationDetailLinkScreen.js';
import Pickup from '../screens/Pickup/Pickup.js';
import SignUpCreateUser from '../screens/Customer/SignUpCreatePasswordCustomer/SignUpCreateUser.js';
import KYCRoute from './Routes/KYCRoute.js';
import ReservationInvitation from '../screens/Reservation/ReservationInvitation/ReservationInvitation.js';
import { getAssociateSaleSetup, logout } from '../actions/associatesActions.js';
import AppUpdateRequestBanner from './AppUpdateRequestBanner';
import { fetchAppStatus } from '../services/user/userService.js';
import ActivityList from '../screens/Activity/ActivityList.js';
import MyNetworkUsers from '../screens/Network/MyNetworkUsers.js';
import CalendarTicketSelector from '../screens/Reservation/CalendarTicketSelector/CalendarTicketSelector.js';
import CampaignsFeedByShow from '../screens/User/Campaigns/CampaignsFeed/CampaignsFeedByShow.js';
import ReferralLink from '../screens/ReferralLink/ReferralLink.js';
import MySimpleNetwork from '../screens/Network/MySimpleNetwork.js';
import Commissions from '../screens/Commissions/Commissions.js';
import SimplePersonalInformation from '../screens/SignUp/SimplePersonalInformation.js';
import LoginCode from '../screens/Login/LoginCode/LoginCode.js';
import StripeOnboardingRefresh from '../screens/Stripe/Onboarding/StripeOnboardingRefresh.js';
import StripeOnboardingReturn from '../screens/Stripe/Onboarding/StripeOnboardingReturn.js';
import AccountDetails from '../screens/Stripe/AccountDetails.js';
import PayoutDetails from '../screens/PayoutDetails/PayoutDetails.js';
import TransferSelector from '../screens/Reservation/Transfer/TransferSelector/TransferSelector.js';
import TransferProductSelector from '../screens/Reservation/Transfer/TransferProductSelector/TransferProductSelector.js';
import TransferReservationCheck from '../screens/Reservation/Transfer/TransferReservationCheck/TransferReservationCheck.js';
import CompleteTransferDetails from '../screens/Reservation/Transfer/CompleteTransferDetails/CompleteTransferDetails.js';
import Additionaltransferinformation from '../screens/Reservation/Transfer/AdditionalTransferInformation/Additionaltransferinformation.js';
import FormCheckTransfer from '../screens/Reservation/ReservationCheck/FormCheckTransfer.js';

const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE);

const App = ({ language }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const associate = useSelector((state) => state.associates);

  const app = useSelector((state) => state.app);
  const user = associate.user;
  const authChecked = associate.authChecked;
  const authenticated = associate.authenticated;
  const status = useSelector((state) => state.app.status);
  const tenantId = useSelector((state) => state.app.tenantId);
  const [loginScreen, setLoginScreen] = useState(history.location.hash === '#login' ? true : false);
  const firebaseApp = initializeApp(firebaseConfig);
  const [startPoint, setStartPoint] = useState(0);
  const [pullChange, setPullChange] = useState();
  const refreshCont = useRef(0);
  const [refresh, setRefresh] = useState(false);
  const isAssociate = user?.roles?.some((r) =>
    ['associate', 'salesteamlead', 'bosssales'].some((role) => r.includes(role)),
  );
  const isCashier = user?.roles?.includes('cashier');
  const isDoorman = user?.roles?.includes('doorman');
  const isCustomer = user?.roles?.includes('customer');
  const isLinkMaker = user?.roles?.includes('link');
  const advantageNetworkStatus = useSelector(
    (state) => state?.associates?.user?.advantageNetworkStatus,
  );

  useScrollHack();
  useAppReload();

  const initLoading = () => {
    // full reload when in home
    if (history?.location?.hash === '' || history?.location?.hash === '#') {
      window.location.reload();
    }

    if (refreshCont && refreshCont.current) {
      refreshCont?.current?.classList.add('loading');
    }
    setTimeout(() => {
      setRefresh(!refresh);
    }, 1000);
  };

  const pullStart = (e) => {
    if (app.canRefresh) {
      const { screenY } = e.targetTouches[0];
      setStartPoint(screenY);
    }
  };

  const pull = (e) => {
    if (app.canRefresh) {
      const touch = e.targetTouches[0];
      const { screenY } = touch;
      const pullLength = startPoint < screenY ? Math.abs(screenY - startPoint) : 0;
      setPullChange(pullLength);
    }
  };

  const endPull = (e) => {
    setStartPoint(0);
    setPullChange(0);
    if (pullChange > 220) initLoading();
  };

  useEffect(() => {
    window.addEventListener('touchstart', pullStart);
    window.addEventListener('touchmove', pull);
    window.addEventListener('touchend', endPull);

    return () => {
      window.removeEventListener('touchstart', pullStart);
      window.removeEventListener('touchmove', pull);
      window.removeEventListener('touchend', endPull);
    };
  });

  useEffect(() => {
    if (app.language !== null) i18n.changeLanguage(app.language);
  }, [app.language]);

  useEffect(() => {
    dispatch(appReady(language));
  }, []);

  useEffect(() => {
    if (history.location.hash === '#login') {
      setLoginScreen(true);
    } else {
      setLoginScreen(false);
    }
  }, [history.location.hash]);

  useEffect(() => {
    if (authenticated) {
      saveToken(associate.jwt);
    }
  }, [authenticated]);

  useEffect(() => {
    if (tenantId) {
      dispatch(fetchAppData(tenantId, language));
    }
  }, [tenantId]);

  const handleVisibilityChange = async (state) => {
    if (associate?.jwt && state === 'visible' && history.location.hash === '') {
      try {
        const result = await fetchAppStatus(associate.jwt);
        if (result.data.userAction === 'REFRESH_USER_DATA')
          dispatch(getAssociateSaleSetup(associate.jwt));
      } catch (e) {}
    }
  };

  useVisibilityChange(handleVisibilityChange);

  const renderLoading = () => {
    let spinner = (
      <CircleSpinner
        className='spinner-app mx-auto'
        style={{ margin: 'auto' }}
        size={40}
        color='var(--color-font-primary)'
      />
    );
    return (
      <div className='loading-app'>
        <div className='loaderDiv'>{spinner}</div>
      </div>
    );
  };

  const renderHomeRoute = () => {
    switch (true) {
      case isCashier:
        return <AuthRoute path='/' render={(props) => <AccountCashier {...props} />} />;
      case isDoorman:
        return <AuthRoute path='/' render={(props) => <AccountChecker {...props} />} />;
      case isCustomer:
        return <AuthRoute path='/' render={(props) => <Home {...props} />} />;
      case isAssociate:
        return <AuthRoute path='/' render={(props) => <Account {...props} />} />;
      case isLinkMaker:
        return <AuthRoute path='/' render={(props) => <LinkHome {...props} />} />;
      default:
        return <AuthRoute path='/' render={(props) => <Account {...props} />} />;
    }
  };

  const onlinePollingDetector = {
    enabled: true,
    interval: 15000,
    url: `https://status.wellet.fun/`,
  };

  const handleSignOut = () => {
    dispatch(logout());
    history.push('/login');
  };

  const renderApp = () => {
    return (
      <DataManagerProvider>
        <AssociateRealtimeProvider>
          <ContextProvider key={refresh}>
            <AppUpdateRequestBanner />
            {isAssociate && advantageNetworkStatus && advantageNetworkStatus !== 'APPROVED' && (
              <div className='censured-text fade-up'>
                <LockIcon size='50px' color='#fff' />
                <div className='text-center mt-4'>
                  <div className='fw-600 font-medium'>{t('advantageNetworkStatus.title')}</div>
                  <div className='font-normal mt-2'>{t('advantageNetworkStatus.subtitle')}</div>
                </div>
                <div className='text-center mt-5 pt-5 text-secondary' onClick={handleSignOut}>
                  {t('menu.logout')}
                </div>
              </div>
            )}

            <div
              className={`main ${loginScreen ? 'login-screen' : 'background-app'} ${
                isAssociate && advantageNetworkStatus && advantageNetworkStatus !== 'APPROVED'
                  ? 'censured-content'
                  : ''
              }`}
            >
              <div
                className='refresh-container'
                ref={refreshCont}
                style={{
                  paddingTop: pullChange / 3.118 / 2 || '',
                  //height: Math.min(pullChange / 3.118, 37) || '',
                }}
              >
                <div className='refresh-icon pt-3'>
                  <RefreshIcon color={'#fff'} pullChange={pullChange} />
                </div>
              </div>

              <HashRouter location={location} hashType='noslash'>
                <Route path='/login' render={(props) => <Login {...props} />} />
                <Route path='/login-user' render={(props) => <LoginUserScreen {...props} />} />
                <Route path='/login-code' render={(props) => <LoginCode {...props} />} />
                {/* <Route path='/login-password' render={(props) => <LoginPassword {...props} />} />
                <Route path='/forgot-password' render={(props) => <ForgotPassword {...props} />} /> */}
                {/* <Route path='/sign-up' render={(props) => <SignUpScreen {...props} />} /> */}
                <Route path='/sign-up-terms' render={(props) => <SignUpTerms {...props} />} />
                <Route
                  path='/terms-and-conditions'
                  render={(props) => <TermsAndConditions {...props} />}
                />
                <Route path='/privacy-policy' render={(props) => <PrivacyPolicy {...props} />} />
                {/* <Route
                  path='/sign-up-phone-validation'
                  render={(props) => <SignUpPhoneValidationScreen {...props} />}
                /> */}
                {/* <Route
                  path='/sign-up-email-validation'
                  render={(props) => <SignUpEmailValidationScreen {...props} />}
                /> */}
                <Route
                  path='/sign-up-email-validation-customer'
                  render={(props) => <SignUpEmailValidationScreenCustomer {...props} />}
                />
                {/* <Route
                  path='/sign-up-create-password-customer'
                  render={(props) => <SignUpCreatePasswordCustomer {...props} />}
                /> */}
                {/* <Route
                  path='/sign-up-create-user'
                  render={(props) => <SignUpCreateUser {...props} />}
                /> */}
                <Route
                  path='/sign-up-concierge-info'
                  render={(props) => <SignUpConciergeScreen {...props} />}
                />
                <Route
                  path='/sign-up'
                  render={(props) => <SimplePersonalInformation {...props} />}
                />
                <Route path='/kyc-start' render={(props) => <StartKyc {...props} />} />
                <Route
                  path='/kyc-validate-front'
                  render={(props) => <FrontValidateKyc {...props} />}
                />
                <Route
                  path='/kyc-validate-back'
                  render={(props) => <BackValidateKyc {...props} />}
                />
                <Route
                  path='/kyc-validate-selfie'
                  render={(props) => <SelfieValidateKyc {...props} />}
                />
                <Route path='/kyc-validate-finish' render={(props) => <FinishKyc {...props} />} />
                <Route path='/market-selector' render={(props) => <MarketSelector {...props} />} />
                <AuthRoute
                  path='/cashier-qr-scan'
                  render={(props) => <QRReaderScreen {...props} />}
                />
                <AuthRoute
                  path='/cashier-operation'
                  render={(props) => <CashierOperation {...props} />}
                />
                <AuthRoute path='/kyc-status' render={(props) => <KycStatus {...props} />} />
                <AuthRoute
                  path='/account-cashier'
                  render={(props) => <AccountCashier {...props} />}
                />
                <AuthRoute
                  path='/cashier-activity'
                  render={(props) => <CashierActivityDetail {...props} />}
                />
                <AuthRoute
                  path='/cashier-payments'
                  render={(props) => <CashierPayments {...props} />}
                />
                {renderHomeRoute()}
                <Route path='/unauthorized' render={(props) => <Unauthorized {...props} />} />
                <AuthRoute path='/notifications' render={(props) => <Notifications {...props} />} />
                <AuthRoute path='/configuration' render={(props) => <Configuration {...props} />} />
                <AuthRoute path='/my-affiliates' render={(props) => <MyAffiliates {...props} />} />
                <AuthRoute path='/city-selector' render={(props) => <CitySelector {...props} />} />
                <AuthRoute path='/welcome' render={(props) => <WelcomeHomeScreen {...props} />} />
                <AuthRoute path='/onboarding' render={(props) => <Onboarding {...props} />} />
                <AuthRoute
                  path='/restaurant-list/:cityId?'
                  render={(props) => <Venues {...props} />}
                />
                <AuthRoute path='/venue' render={(props) => <VenueDetail {...props} />} />
                <AuthRoute path='/event' render={(props) => <EventDetail {...props} />} />
                <AuthRoute
                  path='/reservation-type'
                  render={(props) => <ReservationType {...props} />}
                />
                <AuthRoute
                  path='/reservation-restaurant'
                  render={(props) => <Reservation {...props} />}
                />
                <AuthRoute
                  path='/qr-restaurant'
                  render={(props) => <LinkQrRestaurant {...props} />}
                />
                <AuthRoute
                  path='/event-selector'
                  render={(props) => <EventSelector {...props} />}
                />{' '}
                <AuthRoute path='/link-form' render={(props) => <LinkForm {...props} />} />
                <AuthRoute
                  path='/link-confirmation'
                  render={(props) => <LinkConfirmation {...props} />}
                />
                <AuthRoute path='/referral-link' render={(props) => <ReferralLink {...props} />} />
                <AuthRoute path='/link-home' render={(props) => <LinkHome {...props} />} />
                <AuthRoute
                  path='/reservation/:reservationType'
                  render={(props) => <ReservationArea {...props} />}
                />
                <AuthRoute
                  path='/ticket-selector'
                  render={(props) => <ReservationTicketSelector {...props} />}
                />
                <AuthRoute
                  path='/calendar-ticket-selector'
                  render={(props) => <CalendarTicketSelector {...props} />}
                />
                <AuthRoute
                  path='/transfer-selector'
                  render={(props) => <TransferSelector {...props} />}
                />
                <AuthRoute
                  path='/complete-transfer-details'
                  render={(props) => <CompleteTransferDetails {...props} />}
                />
                <AuthRoute
                  path='/aditional-transfer-information'
                  render={(props) => <Additionaltransferinformation {...props} />}
                />
                <AuthRoute
                  path='/select-transfer'
                  render={(props) => <TransferProductSelector {...props} />}
                />
                <AuthRoute
                  path='/transfer-reservation-check'
                  render={(props) => <TransferReservationCheck {...props} />}
                />
                <AuthRoute
                  path='/form-check-transfer'
                  render={(props) => <FormCheckTransfer {...props} />}
                />
                <RouteReservationDependant
                  path='/area-extra-reservation'
                  render={(props) => <ReservationAreaExtra />}
                  redirectPath='/restaurant-list'
                />
                <RouteReservationDependant
                  path='/reservation-check'
                  render={(props) => <ReservationCheck {...props} />}
                  redirectPath='/restaurant-list'
                />
                <RouteReservationDependant
                  path='/reservation-check-tickets'
                  render={(props) => <ReservationCheckTickets {...props} />}
                />
                <RouteReservationDependant
                  path='/reservation-no-guaranteed'
                  render={(props) => <ReservationNoGuaranteed {...props} />}
                />
                <AuthRoute
                  path='/reservation-invitation'
                  render={(props) => <ReservationInvitation {...props} />}
                />
                <RouteReservationDependant
                  path='/pickup'
                  render={(props) => <Pickup {...props} />}
                />
                <AuthRoute
                  path='/modify-reservation'
                  render={(props) => <ModifyReservation {...props} />}
                />
                <AuthRoute path='/my-money' render={(props) => <Payments {...props} />} />
                <AuthRoute
                  path='/payout-details'
                  render={(props) => <PayoutDetails {...props} />}
                />
                <AuthRoute
                  path='/reservations-disputed'
                  render={(props) => <ReservationsDisputed {...props} />}
                />
                <AuthRoute
                  path='/my-money/detail'
                  render={(props) => <MyMoneyDetail {...props} />}
                />
                <AuthRoute
                  path='/withdrawal-cash'
                  render={(props) => <Withdrawal method='cash' {...props} />}
                />
                <AuthRoute
                  path='/withdrawal-certificate'
                  render={(props) => <Withdrawal method='certificate' {...props} />}
                />
                <AuthRoute
                  path='/mercadopagoaccount'
                  render={(props) => <LinkMercadoPagoAccount {...props} />}
                />
                <Route
                  path='/onboarding-stripe/refresh'
                  render={(props) => <StripeOnboardingRefresh {...props} />}
                />
                <Route
                  path='/onboarding-stripe/return'
                  render={(props) => <StripeOnboardingReturn {...props} />}
                />
                <AuthRoute
                  path='/stripeaccount'
                  render={(props) => <LinkStripeConnect {...props} />}
                />
                <AuthRoute
                  path='/national-bank'
                  render={(props) => <LinkBankNational {...props} />}
                />
                <AuthRoute
                  path='/international-bank'
                  render={(props) => <LinkBankInternational {...props} />}
                />
                <AuthRoute
                  path='/all-reservations'
                  render={(props) => <AllReservations {...props} />}
                />
                <AuthRoute path='/all-activities' render={(props) => <ActivityList {...props} />} />
                <AuthRoute
                  path='/all-payments-link'
                  render={(props) => <LinkPayments {...props} />}
                />
                <AuthRoute path='/activation' render={(props) => <Activation {...props} />} />
                <AuthRoute path='/all-payments' render={(props) => <AllPayments {...props} />} />
                <AuthRoute
                  path='/points-activities'
                  render={(props) => <PointsActivityScreen {...props} />}
                />
                <AuthRoute path='/profile' render={(props) => <UserProfile {...props} />} />
                <AuthRoute
                  path='/my-network-users'
                  render={(props) => <MyNetworkUsers {...props} />}
                />
                {/* <AuthRoute
                  path='/create-network'
                  render={(props) => <CreateNetwork {...props} />}
                /> */}
                <AuthRoute path='/my-network' render={(props) => <MySimpleNetwork {...props} />} />
                <AuthRoute
                  path='/operation-details'
                  render={(props) => <ReservationDetailScreen {...props} />}
                />
                <AuthRoute
                  path='/link-details'
                  render={(props) => <ReservationDetailScreenLink {...props} />}
                />
                <AuthRoute
                  path='/points-details'
                  render={(props) => <PointsDetailsScreen {...props} />}
                />
                <AuthRoute
                  path='/change-password'
                  render={(props) => <UserChangePasswordScreen {...props} />}
                />
                <AuthRoute
                  path='/delete-account'
                  render={(props) => <DeleteAccountScreen {...props} />}
                />
                <AuthRoute
                  path='/onboarding-campaigns'
                  render={(props) => <OnboardingCampaigns {...props} />}
                />
                <AuthRoute
                  path='/campaigns-feed'
                  render={(props) => <CampaignsFeed {...props} />}
                />
                <AuthRoute
                  path='/campaigns-feed-by-show'
                  render={(props) => <CampaignsFeedByShow {...props} />}
                />
                <AuthRoute
                  path='/frequent-questions'
                  render={(props) => <FrequentQuestions {...props} />}
                />
                <AuthRoute
                  path='/reservation-success'
                  render={(props) => <ReservationSuccess {...props} />}
                />
                <AuthRoute
                  path='/reservation-success-courtesy'
                  render={(props) => <ReservationSuccessCourtesy {...props} />}
                />
                <AuthRoute
                  path='/incomes-per-month'
                  render={(props) => <BalancePerMonthScreen {...props} />}
                />
                <AuthRoute
                  path='/incomes-per-month-detail'
                  render={(props) => <BalancePerMonthDetailScreen {...props} />}
                />
                <AuthRoute path={'/commissions'} render={(props) => <Commissions {...props} />} />
                <AuthRoute path={'/menu'} render={() => <Menu history={history} />} />
                {/* Checker routes */}
                <AuthRoute path={'/check-show/:id'} render={(props) => <CheckShow {...props} />} />
                <AuthRoute path={'/create-card'} render={(props) => <CreateCard />} />
                <Route
                  path='/scan-qr-checker/:id'
                  render={(props) => <ScanQrChecker {...props} />}
                />
                <Route
                  path='/manual-qr-checker/:id'
                  render={(props) => <ManualQrChecker {...props} />}
                />
                <Route
                  path='/qr-scan-error'
                  render={(props) => <ScanQrCheckerError {...props} />}
                />
                <Route
                  path='/list-qr-checkin/:id'
                  render={(props) => <ListQrCheckin {...props} />}
                />
                <Route path='/qr-scan-exit' render={(props) => <ScanQrCheckerExit {...props} />} />
                <Route
                  path='/qr-manual-exit'
                  render={(props) => <ManualQrCheckerExit {...props} />}
                />
                <Route path='/check-in-show' render={(props) => <CheckInQr {...props} />} />
                {/* consumer routes */}
                <Route path='/ticket-details' render={(props) => <TicketsDetails {...props} />} />
                <Route
                  path='/ticket-details-success'
                  render={(props) => <TicketsDetailsSuccess {...props} />}
                />
                <Route path='/e-tickets' render={(props) => <Etickets {...props} />} />
                <Route path='/events' render={(props) => <EventsCustomer {...props} />} />
                <Route
                  path='/payment-success'
                  render={(props) => <PayConfirmConsumer {...props} />}
                />
                <AuthRoute
                  path='/notifications-customer'
                  render={(props) => <NotificationsCustomer {...props} />}
                />
                <AuthRoute
                  path='/loyalty-card'
                  render={(props) => <LoyaltyCardCustomer {...props} />}
                />
                <KYCRoute
                  path='/add-personal-information'
                  render={(props) => <PersonalInformationForm {...props} />}
                />
                <AuthRoute path='/add-account' render={(props) => <AddAccount {...props} />} />
                <AuthRoute
                  path='/confirm-add-account'
                  render={(props) => <ConfirmAddAccount {...props} />}
                />
                <AuthRoute
                  path='/success-add-account'
                  render={(props) => <SuccessAddCount {...props} />}
                />
                <AuthRoute
                  path='/account-details'
                  render={(props) => <AccountDetails {...props} />}
                />
                <Route path='/scan-payment' render={(props) => <ScanPaymentScreen {...props} />} />
                <Route path='/consume-summary' render={(props) => <ConsumeSummary {...props} />} />
                <Route path='/consume-pay' render={(props) => <ConsumePay {...props} />} />
                <Route path='/consume-pay-confirm' render={(props) => <Success {...props} />} />
                <Route path='/see-cards' render={(props) => <SeeCards {...props} />} />
                <AuthRoute path={'/points'} render={(props) => <PointsScreen {...props} />} />
                {/* end consumer routes */}
              </HashRouter>
              <div id='bookSelector'></div>
            </div>
          </ContextProvider>
        </AssociateRealtimeProvider>
      </DataManagerProvider>
    );
  };

  return (
    <Detector
      polling={onlinePollingDetector}
      render={({ online }) =>
        online ? (
          <Suspense fallback={renderLoading()}>
            {authChecked &&
            status === 'READY' &&
            (!authenticated || (authenticated && user !== null))
              ? renderApp()
              : renderLoading()}
            <Auth key='auth' />
          </Suspense>
        ) : (
          <div className={'main background-app'}>
            <OfflineScreen />
          </div>
        )
      }
    />
  );
};

export default App;
